import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { Form, Select, Button, DatePicker, Spin, Popover } from 'antd'
import { UserOutlined, DownloadOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import * as XLSX from 'xlsx'
import { getDashboardInfoApi } from '@/service/liveChat/api/getDashboardInfo'
import { getDashboardAgentApi } from '@/service/liveChat/api/getDashboardAgent'
import { getChannelListApi } from '@/service/channel/api/getChannelList'
import { getOnlineAgentsApi } from '@/service/liveChat/api/getOnlineAgents'
import type { DashboardInfoData } from '@/service/liveChat/type/getDashboardInfo'
import type { ChannelItemRes } from '@/service/channel/types/getChannelList'
import searchIcon from '@/assets/images/search-icon.jpg'
import downloadIcon from '@/assets/images/download.png'
import whatsappIcon from '@/assets/images/whatsapp.png'
import telegramIcon from '@/assets/images/telegram.png'
import livechatIcon from '@/assets/images/livechat.png'
import lineIcon from '@/assets/images/line.png'
import styles from './index.module.scss'

const { RangePicker } = DatePicker

const channelOptions = [
    { label: <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><img src={whatsappIcon} style={{ width: 20, height: 20 }} />WhatsApp</span>, value: 'WhatsApp' },
    { label: <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><img src={whatsappIcon} style={{ width: 20, height: 20 }} />WhatsApp Personal</span>, value: 'WhatsAppPersonal' },
    { label: <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><img src={telegramIcon} style={{ width: 20, height: 20 }} />Telegram</span>, value: 'Telegram' },
    { label: <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><img src={livechatIcon} style={{ width: 20, height: 20 }} />WebWidget</span>, value: 'WebWidget' },
    { label: <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><img src={lineIcon} style={{ width: 20, height: 20 }} />Line</span>, value: 'Line' },
]

const ONLINE_AGENTS_POLL_INTERVAL = 15 * 60 * 1000

function formatDuration(seconds: number): string {
    if (!seconds || seconds <= 0) return '0s'
    const totalSeconds = Math.floor(seconds)
    if (totalSeconds < 60) return `${totalSeconds}s`
    const minutes = Math.floor(totalSeconds / 60)
    const secs = totalSeconds % 60
    if (minutes < 60) return secs > 0 ? `${minutes}m ${secs}s` : `${minutes}m`
    const hours = Math.floor(minutes / 60)
    const remainMinutes = minutes % 60
    return remainMinutes > 0 ? `${hours}h ${remainMinutes}m` : `${hours}h`
}

function safeAvg(total: number, count: number): number {
    if (!count || count <= 0) return 0
    return Math.round(total / count)
}

const ChatbotOverview: React.FC = () => {
    const [form] = Form.useForm()
    const [data, setData] = useState<DashboardInfoData | null>(null)
    const [agentData, setAgentData] = useState<Record<string, number> | null>(null)
    const [loading, setLoading] = useState(false)
    const [channelIdList, setChannelIdList] = useState<ChannelItemRes[]>([])
    const [onlineAgents, setOnlineAgents] = useState<string[]>([])
    const onlineTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const onlineCancelledRef = useRef(false)
    const companyName = useSelector((state: any) => state.user.userInfo?.company_name)
    const isAdmin = useSelector((state: any) => state.user.userInfo?.login_account === 'admin')

    const fetchDashboard = useCallback(() => {
        if (!companyName) return
        const values = form.getFieldsValue()
        const [start, end] = values.dateRange || []
        const params = {
            company: companyName,
            start_date: start ? start.format('YYYY-MM-DD 00:00:00') : '',
            end_date: end ? end.format('YYYY-MM-DD 23:59:59') : '',
            channel: values.channel || undefined,
            channel_id: values.channel_id || undefined,
        }
        setLoading(true)
        Promise.all([
            getDashboardInfoApi(params),
            getDashboardAgentApi(params),
        ]).then(([infoRes, agentRes]) => {
            if (infoRes.code === 0) setData(infoRes.data)
            if (agentRes.code === 0) setAgentData(agentRes.data)
        }).finally(() => setLoading(false))
    }, [companyName, form])

    useEffect(() => {
        fetchDashboard()
    }, [companyName, fetchDashboard])

    const fetchOnline = useCallback(async () => {
        if (!companyName) return
        if (onlineTimerRef.current) {
            clearTimeout(onlineTimerRef.current)
            onlineTimerRef.current = null
        }
        try {
            const res = await getOnlineAgentsApi({ company: companyName })
            if (onlineCancelledRef.current) return
            if (res.code === 0) {
                setOnlineAgents(res.data || [])
            }
        } catch (e) {
            // ignore
        } finally {
            if (!onlineCancelledRef.current) {
                onlineTimerRef.current = setTimeout(fetchOnline, ONLINE_AGENTS_POLL_INTERVAL)
            }
        }
    }, [companyName])

    const handleSearch = useCallback(() => {
        fetchDashboard()
        if (isAdmin) fetchOnline()
    }, [fetchDashboard, fetchOnline, isAdmin])

    useEffect(() => {
        if (!companyName || !isAdmin) return
        onlineCancelledRef.current = false

        fetchOnline()

        return () => {
            onlineCancelledRef.current = true
            if (onlineTimerRef.current) {
                clearTimeout(onlineTimerRef.current)
                onlineTimerRef.current = null
            }
        }
    }, [companyName, isAdmin, fetchOnline])

    const handleChannelChange = useCallback((channel: string | undefined) => {
        form.setFieldValue('channel_id', undefined)
        setChannelIdList([])
        if (!channel || !companyName) return
        getChannelListApi({ company: companyName, channel }).then((res) => {
            if (res.code === 0) {
                setChannelIdList(res.data.list)
            }
        })
    }, [companyName, form])

    const transferSuccessRate = useMemo(() => {
        if (!data || !data.transfer_requested) return '0%'
        return ((data.transfer_success / data.transfer_requested) * 100).toFixed(1) + '%'
    }, [data])

    const transferRate = useMemo(() => {
        if (!data || !data.session_cnt) return '0%'
        return ((data.transfer_requested / data.session_cnt) * 100).toFixed(1) + '%'
    }, [data])

    const buildExportFilename = useCallback(() => {
        const values = form.getFieldsValue()
        const [start, end] = values.dateRange || []

        const parts: string[] = []

        if (companyName) {
            parts.push(String(companyName))
        }

        if (start && end) {
            parts.push(`${start.format('YYYYMMDD')}-${end.format('YYYYMMDD')}`)
        }

        if (values.channel) {
            parts.push(values.channel)
        }

        if (values.channel_id) {
            const found = channelIdList.find((c) => c.channel_id === values.channel_id)
            parts.push(String(found?.channel_name || values.channel_id))
        }

        const raw = parts.join('_') || 'dashboard'
        const safe = raw.replace(/[\\/:*?"<>|\s]+/g, '_')

        return `${safe}.xlsx`
    }, [form, companyName, channelIdList])

    const handleExport = useCallback(() => {
        if (!data) return

        const values = form.getFieldsValue()
        const [start, end] = values.dateRange || []
        const startStr = start ? start.format('YYYY-MM-DD') : ''
        const endStr = end ? end.format('YYYY-MM-DD') : ''

        const botAvg = formatDuration(safeAvg(data.bot_response_duration, data.bot_response_total))
        const agentAvg = formatDuration(safeAvg(data.agent_response_duration, data.agent_response_total))

        // Sheet 1: Overview Metrics (两行：第一行指标名，第二行对应数值)
        const pairs: [string, string | number][] = [
            ['Date Range', `${startStr} ~ ${endStr}`],
            ['Channel', values.channel || 'All'],
            ['Channel ID', values.channel_id || 'All'],

            ['TOTAL USERS', data.user_cnt],
            ['TOTAL MESSAGES', data.msg_cnt],
            ['TOTAL SESSION', data.session_cnt],
            ['LIVE AGENT TOTAL SESSION', data.transfer_requested],

            ['BOT HANDLE DURATION', formatDuration(data.bot_handle_duration)],
            ['BOT RESPONSE DURATION', formatDuration(data.bot_response_duration)],
            ['BOT RESPONSE COUNT', data.bot_response_total],
            ['BOT AVG. RESPONSE TIME', botAvg],

            ['AGENT HANDLE DURATION', formatDuration(data.agent_handle_duration)],
            ['AGENT RESPONSE DURATION', formatDuration(data.agent_response_duration)],
            ['AGENT RESPONSE COUNT', data.agent_response_total],
            ['AGENT AVG. RESPONSE TIME', agentAvg],

            ['TRANSFER REQUESTED', data.transfer_requested],
            ['TRANSFER SUCCESS', data.transfer_success],
            ['TRANSFER SUCCESS RATE', transferSuccessRate],
            ['HANDOFF RATE', transferRate],
        ]

        const overviewAoa: (string | number)[][] = [
            pairs.map(([label]) => label),
            pairs.map(([, value]) => value),
        ]

        const wsOverview = XLSX.utils.aoa_to_sheet(overviewAoa)
        wsOverview['!cols'] = pairs.map(() => ({ wch: 22 }))

        // Sheet 2: Agent Workload
        const entries = Object.entries(agentData || {}).sort(([, a], [, b]) => b - a)
        const total = entries.reduce((sum, [, v]) => sum + v, 0)
        const agentAoa: (string | number)[][] = [
            ['Agent', 'Sessions', 'Share'],
            ...entries.map(([name, count]) => [
                name,
                count,
                total ? `${((count / total) * 100).toFixed(1)}%` : '0%',
            ]),
            ['Total', total, '100%'],
        ]
        const wsAgent = XLSX.utils.aoa_to_sheet(agentAoa)
        wsAgent['!cols'] = [{ wch: 24 }, { wch: 12 }, { wch: 10 }]

        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, wsOverview, 'Overview Metrics')
        XLSX.utils.book_append_sheet(wb, wsAgent, 'Agent Workload')

        XLSX.writeFile(wb, buildExportFilename())
    }, [data, agentData, form, transferSuccessRate, transferRate, buildExportFilename])

    return (
        <div className={styles.dashboardPage}>
            <div className={styles.searchWrapper}>
                <Form
                    form={form}
                    layout="inline"
                    className={styles.searchForm}
                    initialValues={{
                        dateRange: [dayjs().subtract(7, 'day'), dayjs()],
                    }}
                >
                    <Form.Item name="dateRange">
                        <RangePicker
                            format="YYYY-MM-DD"
                            allowClear={false}
                            className={styles.dateRangePicker}
                            popupClassName={styles.dateRangePopup}
                        />
                    </Form.Item>
                    <Form.Item name="channel">
                        <Select
                            options={channelOptions}
                            placeholder="Select Channel"
                            allowClear
                            className={styles.searchInput}
                            prefix={<img src={searchIcon} style={{ width: 14, height: 14 }} />}
                            onChange={handleChannelChange}
                        />
                    </Form.Item>
                    <Form.Item name="channel_id">
                        <Select
                            placeholder="Select Channel ID"
                            allowClear
                            className={styles.searchInput}
                            prefix={<img src={searchIcon} style={{ width: 14, height: 14 }} />}
                            options={channelIdList.map((item) => ({
                                label: item.channel_name || item.channel_id,
                                value: item.channel_id,
                            }))}
                            notFoundContent={null}
                        />
                    </Form.Item>
                    <Form.Item>
                        <Button
                            type="primary"
                            style={{ gap: 2 }}
                            icon={<img src={searchIcon} style={{ width: 14, height: 14 }} />}
                            onClick={handleSearch}
                        >
                            Search
                        </Button>
                    </Form.Item>
                </Form>
            </div>

            <Spin spinning={loading} wrapperClassName={styles.spinWrapper}>
                {isAdmin && (
                    <div className={styles.livePanel}>
                        <div className={styles.liveRadar}>
                            <span className={styles.radarDot} />
                            <span className={styles.radarRing} />
                            <span
                                className={styles.radarRing}
                                style={{ animationDelay: '0.6s' }}
                            />
                            <span
                                className={styles.radarRing}
                                style={{ animationDelay: '1.2s' }}
                            />
                        </div>

                        <div className={styles.liveBrand}>
                            <span className={styles.liveTag}>LIVE NOW</span>
                        </div>

                        <div className={styles.liveDivider} />

                        <div className={styles.liveMetric}>
                            <span className={`${styles.liveMetricValue} ${styles.agentColor}`}>
                                {onlineAgents.length}
                            </span>
                            <span className={styles.liveMetricLabel}>Agents Online</span>
                        </div>

                        {onlineAgents.length > 0 ? (
                            <div className={styles.liveNames}>
                                {onlineAgents.slice(0, 5).map((name) => (
                                    <span
                                        key={name}
                                        className={styles.liveNameChip}
                                        title={name}
                                    >
                                        <span className={styles.liveNameDot} />
                                        <span className={styles.liveNameText}>{name}</span>
                                    </span>
                                ))}
                                {onlineAgents.length > 5 && (
                                    <Popover
                                        trigger="click"
                                        placement="bottomRight"
                                        overlayClassName={styles.livePopover}
                                        content={
                                            <div className={styles.livePopoverContent}>
                                                <div className={styles.livePopoverTitle}>
                                                    <span className={styles.livePopoverDot} />
                                                    All Online Agents
                                                    <span className={styles.livePopoverCount}>
                                                        {onlineAgents.length}
                                                    </span>
                                                </div>
                                                <div className={styles.livePopoverList}>
                                                    {onlineAgents.map((name) => (
                                                        <span
                                                            key={name}
                                                            className={styles.liveNameChip}
                                                            title={name}
                                                        >
                                                            <span className={styles.liveNameDot} />
                                                            <span className={styles.liveNameText}>{name}</span>
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        }
                                    >
                                        <span className={`${styles.liveNameChip} ${styles.liveNameMore}`}>
                                            +{onlineAgents.length - 5}
                                        </span>
                                    </Popover>
                                )}
                            </div>
                        ) : (
                            <span className={styles.liveEmpty}>No agents online</span>
                        )}
                    </div>
                )}

                {data && (
                    <>
                        {/* 4×4 Card Grid */}
                        <div className={styles.cardGrid}>
                            <div className={styles.gridCard}>
                                <span className={styles.gridCardLabel}>TOTAL USERS</span>
                                <span className={styles.gridCardValue}>{data.user_cnt.toLocaleString()}</span>
                            </div>
                            <div className={styles.gridCard}>
                                <span className={styles.gridCardLabel}>TOTAL MESSAGES</span>
                                <span className={styles.gridCardValue}>{data.msg_cnt.toLocaleString()}</span>
                            </div>
                            <div className={styles.gridCard}>
                                <span className={styles.gridCardLabel}>TOTAL SESSION</span>
                                <span className={styles.gridCardValue}>{data.session_cnt.toLocaleString()}</span>
                            </div>
                            <div className={styles.gridCard}>
                                <span className={styles.gridCardLabel}>LIVE AGENT TOTAL SESSION</span>
                                <span className={styles.gridCardValue}>{data.transfer_requested.toLocaleString()}</span>
                            </div>

                            <div className={styles.gridCard}>
                                <span className={styles.gridCardLabel}>BOT HANDLE DURATION</span>
                                <span className={styles.gridCardValueBot}>{formatDuration(data.bot_handle_duration)}</span>
                            </div>
                            <div className={styles.gridCard}>
                                <span className={styles.gridCardLabel}>BOT RESPONSE DURATION</span>
                                <span className={styles.gridCardValueBot}>{formatDuration(data.bot_response_duration)}</span>
                            </div>
                            <div className={styles.gridCard}>
                                <span className={styles.gridCardLabel}>BOT RESPONSE COUNT</span>
                                <span className={styles.gridCardValueBot}>{data.bot_response_total.toLocaleString()}</span>
                            </div>
                            <div className={styles.gridCard}>
                                <span className={styles.gridCardLabel}>BOT AVG. RESPONSE TIME</span>
                                <span className={styles.gridCardValueBot}>
                                    {formatDuration(safeAvg(data.bot_response_duration, data.bot_response_total))}
                                </span>
                            </div>

                            <div className={styles.gridCard}>
                                <span className={styles.gridCardLabel}>AGENT HANDLE DURATION</span>
                                <span className={styles.gridCardValueAgent}>{formatDuration(data.agent_handle_duration)}</span>
                            </div>
                            <div className={styles.gridCard}>
                                <span className={styles.gridCardLabel}>AGENT RESPONSE DURATION</span>
                                <span className={styles.gridCardValueAgent}>{formatDuration(data.agent_response_duration)}</span>
                            </div>
                            <div className={styles.gridCard}>
                                <span className={styles.gridCardLabel}>AGENT RESPONSE COUNT</span>
                                <span className={styles.gridCardValueAgent}>{data.agent_response_total.toLocaleString()}</span>
                            </div>
                            <div className={styles.gridCard}>
                                <span className={styles.gridCardLabel}>AGENT AVG. RESPONSE TIME</span>
                                <span className={styles.gridCardValueAgent}>
                                    {formatDuration(safeAvg(data.agent_response_duration, data.agent_response_total))}
                                </span>
                            </div>

                            <div className={styles.gridCard}>
                                <span className={styles.gridCardLabel}>TRANSFER REQUESTED</span>
                                <span className={styles.gridCardValue}>{data.transfer_requested.toLocaleString()}</span>
                            </div>
                            <div className={styles.gridCard}>
                                <span className={styles.gridCardLabel}>TRANSFER SUCCESS</span>
                                <span className={styles.gridCardValue}>{data.transfer_success.toLocaleString()}</span>
                            </div>
                            <div className={styles.gridCard}>
                                <span className={styles.gridCardLabel}>TRANSFER SUCCESS RATE</span>
                                <span className={styles.gridCardValue}>{transferSuccessRate}</span>
                            </div>
                            <div className={styles.gridCard}>
                                <span className={styles.gridCardLabel}>HANDOFF RATE</span>
                                <span className={styles.gridCardValue}>{transferRate}</span>
                                <span className={styles.gridCardSub}>Transferred / Total Sessions</span>
                            </div>
                        </div>

                        {/* Detailed Session Metrics */}
                        <div className={styles.metricsCard}>
                            <div className={styles.cardTitle}>
                                <span className={styles.titleDot} />Detailed Session Metrics
                            </div>
                            <table className={styles.metricsTable}>
                                <thead>
                                    <tr>
                                        <th>METRIC TYPE</th>
                                        <th>TOTAL</th>
                                        <th>BOT ACTIVE</th>
                                        <th>TRANSFERRING</th>
                                        <th>TAKEOVER</th>
                                        <th>CLOSED</th>
                                        <th>WAITING</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>All Sessions</td>
                                        <td className={styles.bold}>{data.session_cnt}</td>
                                        <td className={styles.blue}>{data.bot}</td>
                                        <td className={styles.orange}>{data.manualing}</td>
                                        <td className={styles.purple}>{data.manualed}</td>
                                        <td>{data.closed}</td>
                                        <td className={styles.cyan}>{data.wait}</td>
                                    </tr>
                                    <tr>
                                        <td>Live Agent Takeover</td>
                                        <td className={styles.bold}>{data.transfer_requested}</td>
                                        <td className={styles.blue}>-</td>
                                        <td className={styles.orange}>{data.manualing}</td>
                                        <td className={styles.purple}>{data.transfer_success}</td>
                                        <td>-</td>
                                        <td className={styles.cyan}>-</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div className={styles.bottomRow}>
                            {/* Transfer Reasons */}
                            <div className={styles.transferCard}>
                                <div className={styles.cardTitle}>
                                    <span className={styles.titleDot} />Transfer Reasons
                                </div>
                                <div className={styles.reasonList}>
                                    {data.reason_top_n && data.reason_top_n.length > 0 ? (
                                        data.reason_top_n.map((reason, idx) => (
                                            <div key={idx} className={styles.reasonItem}>
                                                <span className={styles.reasonRank}>#{idx + 1}</span>
                                                <span className={styles.reasonText}>{reason}</span>
                                            </div>
                                        ))
                                    ) : (
                                        <div className={styles.emptyText}>No data</div>
                                    )}
                                </div>
                            </div>

                            {/* Agent Workload */}
                            <div className={styles.transferCard}>
                                <div className={styles.cardTitleRow}>
                                    <div className={styles.cardTitle}>
                                        <span className={styles.titleDot} />Agent Workload
                                    </div>
                                    {agentData && Object.keys(agentData).length > 0 && (
                                        <div className={styles.agentSummary}>
                                            <span className={styles.agentSummaryNum}>
                                                {Object.keys(agentData).length}
                                            </span>
                                            agents
                                            <span className={styles.agentSummaryDot}>·</span>
                                            <span className={styles.agentSummaryNum}>
                                                {Object.values(agentData).reduce((a, b) => a + b, 0).toLocaleString()}
                                            </span>
                                            total sessions
                                        </div>
                                    )}
                                </div>
                                <div className={styles.agentList}>
                                    {agentData && Object.keys(agentData).length > 0 ? (
                                        (() => {
                                            const entries = Object.entries(agentData).sort(([, a], [, b]) => b - a)
                                            const max = entries[0]?.[1] || 1
                                            const total = entries.reduce((sum, [, v]) => sum + v, 0)
                                            return entries.map(([agent, count]) => {
                                                const percent = total ? (count / total) * 100 : 0
                                                const share = total ? ((count / total) * 100).toFixed(1) : '0'
                                                return (
                                                    <div key={agent} className={styles.agentItem}>
                                                        <span className={styles.agentAvatar}>
                                                            <UserOutlined />
                                                        </span>
                                                        <div className={styles.agentMain}>
                                                            <div className={styles.agentTopRow}>
                                                                <span className={styles.agentName}>{agent}</span>
                                                                <span className={styles.agentCount}>
                                                                    {count}
                                                                    <em className={styles.agentShare}> · {share}%</em>
                                                                </span>
                                                            </div>
                                                            <div className={styles.agentBarTrack}>
                                                                <div
                                                                    className={styles.agentBarFill}
                                                                    style={{ width: `${percent}%` }}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                )
                                            })
                                        })()
                                    ) : (
                                        <div className={styles.emptyText}>No data</div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div
                            className={styles.downloadWrapper}
                            onClick={handleExport}
                        >
                            <DownloadOutlined />
                            <span>Output Data</span>
                        </div>
                    </>
                )}
            </Spin>
        </div>
    )
}

export default ChatbotOverview